import { useState } from 'react';

import { isNil } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { PAGE_STATUS_DRAFT, PAGE_STATUS_REJECTED, PAGE_TYPE_PROPERTY, LIBRARY_RESOURCE } from 'constants/index';
import { PAGE_DETAILS_PATH, RESOURCE_LIBRARY_PATH, CONTENT_LIBRARY_PATH } from 'constants/routes';
import { EDITING_PERMISSION_BLOCK, EDITING_PERMISSION_TEXT } from 'constants/texts';
import { useAccountPath } from 'hooks/useAccountPath';
import { useSnackbar } from 'hooks/useSnackbar';
import { pageDataSelector } from 'store/page/selectors';
import { deletePageThunk, publishPageThunk, savePageThunk } from 'store/page/thunks';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { isOwnerSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import { cleanUpArrayFields } from 'utils/form';
import { removeAIBlocksFromPage } from 'utils/itineraries/functions/builderUtils';
import logger from 'utils/logger';
import { mapLibType2LibPath } from 'utils/map';

import { STEP_PATHS } from './map';

import { usePageContext } from '.';

const isThereNeedToShowIncompleteAreaAlert = (page) => {
  return (
    page.type === PAGE_TYPE_PROPERTY &&
    !isNil(page.areaId) &&
    !isNil(page.areaPageId) &&
    page.policies?.areaPageUpdate &&
    (page.areaPageStatus === PAGE_STATUS_DRAFT || page.areaPageStatus === PAGE_STATUS_REJECTED)
  );
};

function getErrorsMap(source = {}) {
  return Object.keys(source).reduce((res, key) => {
    res[key] = source[key][0];

    return res;
  }, {});
}

const useDeletePageHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const page = useSelector(pageDataSelector);
  const getPath = useAccountPath();
  const { libType } = useParams();
  const libURL = mapLibType2LibPath(libType);
  const onSnackbar = useSnackbar();

  const handleDelete = () => {
    confirmAlert({
      title: 'Do you want to delete this page now?',
      onConfirm: async () => {
        try {
          await dispatch(deletePageThunk(page.id));
          dispatch(setAppHasChangesAction(false));

          // before redirecting we need to be sure that "app has changes" is set to false
          // and user won't get an alert
          setTimeout(() => {
            history.push(getPath(libURL));
            onSnackbar(`Page '${page.name}' has been deleted.`);
          }, 0);
        } catch (error) {
          if (error?.errors?.title?.includes(EDITING_PERMISSION_TEXT)) {
            onSnackbar(EDITING_PERMISSION_BLOCK, true);
          } else {
            onSnackbar(`Page '${page.name}' couldn't be deleted. Please try again.`, true);
          }
          throw error;
        }
      },
    });
  };

  return handleDelete;
};

const useNextStepHandler = (params = {}) => {
  const { onBeforeNext, onAfterNext, onNextError } = params;
  const dispatch = useDispatch();
  const { currentStep, nextStep } = usePageContext();
  const getPath = useAccountPath();
  const { libType } = useParams();
  const history = useHistory();
  const onSnackbar = useSnackbar();
  const doesPageHaveChanges = useSelector(appHasChangesSelector);

  const handleNext = async (newPage) => {
    try {
      if (onBeforeNext) onBeforeNext(newPage);
      const savedPage = await dispatch(savePageThunk({ ...newPage, currentStep }));
      if (onAfterNext) onAfterNext(savedPage, { pageHadChangesBeforeSaving: doesPageHaveChanges });
      const nextPath = STEP_PATHS[nextStep];
      history.push(getPath(nextPath, { libType, pageId: savedPage.pageId }));
    } catch (error) {
      if (onNextError) {
        onNextError(error);
      } else {
        if (error?.errors?.title?.includes(EDITING_PERMISSION_TEXT)) {
          onSnackbar(EDITING_PERMISSION_BLOCK, true);
        } else {
          onSnackbar("Page couldn't be saved. Please try again.", true);
        }
      }
      throw error;
    }
  };

  return handleNext;
};

export const useNextStepFormHandler = (params = {}) => {
  const handleNextStep = useNextStepHandler({ isFormHandler: true, ...params });

  return async (values) => {
    try {
      await handleNextStep(cleanUpArrayFields(values));
    } catch (error) {
      const { errors } = error;
      logger.error(error);
      return getErrorsMap(errors?.source);
    }
  };
};

const useSavePageHandler = (params = {}) => {
  const { onBeforeSave, onAfterSave, onSaveError } = params;
  const dispatch = useDispatch();
  const { currentStep } = usePageContext();
  const onSnackbar = useSnackbar();
  const history = useHistory();
  const getPath = useAccountPath();
  const doesPageHaveChanges = useSelector(appHasChangesSelector);

  const handleSave = async (newPage, isThrowError = false) => {
    try {
      if (onBeforeSave) onBeforeSave(newPage);
      const page = removeAIBlocksFromPage(cleanUpArrayFields(newPage));
      const savedPage = await dispatch(savePageThunk({ ...page, currentStep }));
      if (onAfterSave) onAfterSave(savedPage, { pageHadChangesBeforeSaving: doesPageHaveChanges });
      onSnackbar(`Page '${savedPage.name}' has been saved.`);

      const showIncompleteAreaAlert = (page) => {
        confirmAlert({
          title: `Please complete the Area page "${page.areaName}" now.`,
          confirmButtonText: 'Let’s Go!',
          onConfirm: () => {
            history.push(getPath(PAGE_DETAILS_PATH, { pageId: page.areaPageId }));
          },
        });
      };

      const shouldShowIncompleteAreaAlert = isThereNeedToShowIncompleteAreaAlert(savedPage);
      if (shouldShowIncompleteAreaAlert) {
        showIncompleteAreaAlert(savedPage);
      }
    } catch (error) {
      if (onSaveError) {
        onSaveError(error);
      } else {
        if (error?.errors?.title?.includes(EDITING_PERMISSION_TEXT)) {
          onSnackbar(EDITING_PERMISSION_BLOCK, true);
        } else {
          onSnackbar("Page couldn't be saved. Please try again.", true);
        }
      }

      if (isThrowError) throw error;
    }
  };

  return handleSave;
};

const usePublishPageHandler = (params = {}) => {
  const { onBeforePublish, onAfterPublish, onPublishError, isFromSave = false, withConfirmation = false } = params;
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const onSnackbar = useSnackbar();
  const { currentStep } = usePageContext();
  const isOwner = useSelector(isOwnerSelector);
  const history = useHistory();
  const getPath = useAccountPath();
  const { libType } = useParams();
  const libURL = libType === LIBRARY_RESOURCE ? RESOURCE_LIBRARY_PATH : CONTENT_LIBRARY_PATH;

  const publish = async (newPage) => {
    setLoading(true);
    try {
      if (onBeforePublish) onBeforePublish(newPage);

      if (!isFromSave) await dispatch(savePageThunk({ ...cleanUpArrayFields(newPage), currentStep }));
      const publishedPage = await dispatch(publishPageThunk());

      if (onAfterPublish) onAfterPublish(publishedPage);

      if (isOwner) {
        onSnackbar(`Page '${publishedPage.name}' has been sent for approval.`);
      } else {
        onSnackbar(`Page '${publishedPage.name}' has been published.`);
      }

      const showOwnerAlert = () => {
        confirmAlert({
          title: (
            <>
              Thank you for creating your content
              <br />
              and building our community.
            </>
          ),
          hint: (
            <>
              Your content has been sent to Safari Portal for review and
              <br />
              you will receive a message from us with an approval or
              <br />
              request for edits within 48 hours.
            </>
          ),
          confirmButtonText: 'Great',
          showCancel: false,
          onConfirm: () => (window.location = getPath(libURL)),
          onManualClose: () => (window.location = getPath(libURL)),
        });
      };
      const showIncompleteAreaAlert = (page) => {
        confirmAlert({
          title: `Please complete the Area page "${page.areaName}" now.`,
          confirmButtonText: 'Let’s Go!',
          onConfirm: () => {
            history.push(getPath(PAGE_DETAILS_PATH, { pageId: page.areaPageId }));
          },
          onManualClose: () => {
            if (isOwner) showOwnerAlert();
          },
          onCancel: () => {
            if (isOwner) showOwnerAlert();
          },
        });
      };

      const shouldShowIncompleteAreaAlert = isThereNeedToShowIncompleteAreaAlert(publishedPage);
      if (isOwner && !shouldShowIncompleteAreaAlert) {
        showOwnerAlert();
      } else if (shouldShowIncompleteAreaAlert) {
        showIncompleteAreaAlert(publishedPage);
      } else {
        window.location = getPath(libURL);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (onPublishError) onPublishError(error);
      onSnackbar(`Page '${newPage.name}' couldn't be published. Please try again.`, true);
      throw error;
    }
  };

  const handlePublish = async (newPage) => {
    if (isFromSave || !withConfirmation) {
      await publish(newPage);
      setLoading(false);
    } else {
      confirmAlert({
        title: 'Do you want to publish this page now?',
        hintBottom:
          'Publishing will send this page to Safari Portal for approval and you will not be able to make more changes until the content has been approved.',
        onConfirm: async () => {
          await publish(newPage);
        },
      });
    }
  };

  return { handler: handlePublish, isLoading };
};

export { useNextStepHandler, useDeletePageHandler, usePublishPageHandler, useSavePageHandler };
